import { Loader2, RefreshCw, Wifi, WifiOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { ConnectionStatus } from '@/hooks/useOpsSnapshot';
import { cn } from '@/lib/utils';

const STATUS_CONFIG: Record<ConnectionStatus, { label: string; icon: typeof Wifi; color: string }> = {
  connecting: { label: 'Connecting', icon: Loader2, color: 'text-amber-500' },
  live: { label: 'Live', icon: Wifi, color: 'text-emerald-500' },
  reconnecting: { label: 'Reconnecting', icon: RefreshCw, color: 'text-amber-500' },
  offline: { label: 'Offline', icon: WifiOff, color: 'text-red-500' },
};

export function ConnectionStatusChip({
  status,
  lastUpdatedAt,
  onReconnect,
}: {
  status: ConnectionStatus;
  lastUpdatedAt: Date | null;
  onReconnect: () => void;
}) {
  const ageMs = lastUpdatedAt ? Date.now() - lastUpdatedAt.getTime() : null;
  const ageSeconds = ageMs !== null ? Math.floor(ageMs / 1000) : null;
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  const isAnimated = status === 'connecting' || status === 'reconnecting';

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1.5">
        <Icon className={cn('h-4 w-4', config.color, isAnimated && 'animate-spin')} />
        <span className={cn('text-sm font-medium', config.color)}>{config.label}</span>
      </div>
      {ageSeconds !== null && (
        <span className="text-xs text-muted-foreground">
          as of {ageSeconds < 5 ? 'now' : `${ageSeconds}s ago`}
        </span>
      )}
      {(status === 'offline' || status === 'reconnecting') && (
        <Button variant="ghost" size="sm" onClick={onReconnect} className="h-7 px-2 text-xs">
          Retry
        </Button>
      )}
    </div>
  );
}
